// Hub is always the top-level document (unlike Suite, which is embedded in
// Hub's iframe and needs geometry relayed via postMessage - see Suite's own
// utils/wcoGeometry.ts for that side), so it can read
// navigator.windowControlsOverlay directly and doesn't need any relay.
import { useEffect, useState } from 'react';

export interface TitlebarGeometry {
  active: boolean;
  height: number | null;
  x: number | null;
  width: number | null;
}

// navigator.windowControlsOverlay.getTitlebarAreaRect() under-reports usable
// width on macOS specifically: Chrome's own app-affordance icons there
// (chevron/key/menu) aren't real OS window-caption buttons the way Windows'
// minimize/maximize/close row is, so they're not part of the officially
// reported WCO geometry at all - but Chrome still reserves blank space on
// the right as if a Windows-style button row were present, leaving the
// header with unused space before those icons. Windows' real caption
// buttons ARE included in the reported rect, so no correction is needed
// there. Approximate starting value - tune visually, there's no API that
// reports the exact on-screen width of Chrome's own icon row.
const MAC_WCO_WIDTH_CORRECTION = 90;

export const isMacPlatform = (): boolean => {
  const uaData = (navigator as any).userAgentData;
  if (uaData?.platform) return uaData.platform === 'macOS';
  return /Mac/.test(navigator.platform || navigator.userAgent);
};

export const correctedWcoWidth = (rawWidth: number): number =>
  isMacPlatform() ? rawWidth + MAC_WCO_WIDTH_CORRECTION : rawWidth;

const measure = (): TitlebarGeometry => {
  const wco = (navigator as any).windowControlsOverlay;
  if (!wco || !wco.visible) return { active: false, height: null, x: null, width: null };
  const rect = wco.getTitlebarAreaRect();
  return {
    active: true,
    height: Math.round(rect.height),
    x: Math.round(rect.x),
    width: Math.round(correctedWcoWidth(rect.width)),
  };
};

// Live-updating geometry for Hub's own top-level header. Hub can listen to
// geometrychange directly since it IS the top-level document (contrast with
// Suite's embedded header, which needs Hub to relay this via postMessage
// since a cross-origin iframe can't read WCO state itself).
export function useLiveTitlebarGeometry(): TitlebarGeometry {
  const [geo, setGeo] = useState<TitlebarGeometry>(() => measure());

  useEffect(() => {
    const wco = (navigator as any).windowControlsOverlay;
    if (!wco) return undefined;
    const handleChange = () => setGeo(measure());
    wco.addEventListener('geometrychange', handleChange);
    return () => wco.removeEventListener('geometrychange', handleChange);
  }, []);

  return geo;
}
